<template>
  <div class="lp-element-panel lp-shape-panel">
    <field-group
        v-if="!isMultiPropertySelection"
        label="Add">
      <div class="lp-shape-add">
        <button
            v-tooltip="'Add Circle'"
            type="button"
            class="lp-shape-add__btn"
            @click.stop="addShapeObject('circle')">
          <icon name="circle"/>
          <span>Circle</span>
        </button>
        <button
            v-tooltip="'Add Rectangle'"
            type="button"
            class="lp-shape-add__btn"
            @click.stop="addShapeObject('rectangle')">
          <icon name="rectangle"/>
          <span>Rectangle</span>
        </button>
      </div>
      <button
          type="button"
          class="lp-shape-library"
          @click="toggleShapeModel">
        <icon name="layers"/>
        <span>Shape library</span>
      </button>
    </field-group>

    <template v-if="activeObject">
      <div
          v-if="!isMultiPropertySelection"
          class="lp-shape-divider"/>

      <fill-color-section
          :open="fillOpen"
          title="Fill color"
          @toggle="$emit('toggle-fill')">
        <color-picker
            :color="getColorLabelShape"
            :type="getObjectType"
            :key="'color-picker-'+((activeObjectForLeftPanel && activeObjectForLeftPanel.Id) || 'none')+'-'+((activeObjectForLeftPanel && activeObjectForLeftPanel.isGradient) || false)+'-'+getSelectedPointOfGradient"
            :active-object="activeObjectForLeftPanel"
            :selected-objects-list="getSelectedObjectsList"
            :content-list="contentList"
            display-style="inline"
            variant="panel"
            color-type="fill"
            @updateSelectedPointOfGradient="updateSelectedPointOfGradient"
            @updateSelectedPointPosition="updateSelectedPointPosition"
            @changeMode="handleModeChange"
            @updateSelectedGradientAngle="updateSelectedGradientAngle"
        />
      </fill-color-section>

      <field-group label="Effects">
        <effect-tiles
            :options="shapeEffectOptions"
            :value="shapeEffectValue"
            :columns="3"
            @select="onShapeEffectSelect"/>
        <effect-control-card
            v-if="shapeEffectValue === 'outline'"
            :rows="shapeOutlineRows"
            :color="outlineSwatchColor"
            class="lp-shape-panel__effect-card"
            color-aria="Open outline color picker"
            show-color
            @input="onShapeOutlineInput"
            @pick="openImageEffectColorPicker"/>
        <effect-control-card
            v-if="shapeEffectValue === 'shadow'"
            :rows="imageShadowRows"
            :color="tempShapeShadowColor"
            class="lp-shape-panel__effect-card"
            color-aria="Open shadow color picker"
            show-color
            @input="onShapeShadowInput"
            @pick="openImageEffectColorPicker"/>
      </field-group>
      <side-popover
          ref="imageEffectColorPopoverEl"
          :open="isColorPickerOpen && (shapeEffectValue === 'outline' || shapeEffectValue === 'shadow')"
          :top="imageEffectColorPopover.top"
          :left="imageEffectColorPopover.left"
          :width="303"
          :title="shapeEffectValue === 'outline' ? 'Outline color' : 'Shadow color'"
          @close="isColorPickerOpen = false"
          @click.native.stop>
        <color-picker
            v-if="shapeEffectValue === 'outline'"
            :color="getStrokeColorLabelShape"
            :type="getObjectType"
            :key="'color-picker-stroke-'+((activeObjectForLeftPanel && activeObjectForLeftPanel.Id) || 'none')"
            :active-object="isMultiPropertySelection ? activeObjectForLeftPanel : undefined"
            :selected-objects-list="getSelectedObjectsList"
            :content-list="contentList"
            display-style="inline"
            variant="panel"
            color-type="outline"
            @color-changed="onShapeOutlineColorChanged"
        />
        <color-picker
            v-else
            :active-object="activeObjectForLeftPanel"
            :color="tempShapeShadowColor"
            :type="getObjectType"
            :key="'color-picker-shadow-non-text-object-effect-'+((activeObjectForLeftPanel && activeObjectForLeftPanel.Id) || 'none')"
            :selected-objects-list="getSelectedObjectsList"
            :content-list="contentList"
            :show-gradient="false"
            color-type="shadow"
            display-style="inline"
            variant="panel"
        />
      </side-popover>
    </template>

    <field-group
        v-if="isMultiPropertySelection || activeObjectForLeftPanel"
        label="Position on page">
      <position-controls
          :horizontal="shownAlignment.horizontal"
          :vertical="shownAlignment.vertical"
          @align="onPositionAlign"/>
    </field-group>

    <field-group
        v-if="!isMultiPropertySelection && activeObjectForLeftPanel"
        label="Rotate & flip">
      <rotate-flip-controls
          @rotate="(dir) => dir === 'left' ? rotateLeft() : rotateRight()"
          @flip="(dir) => dir === 'horizontal' ? flipHorizontal() : flipVertical()"/>
    </field-group>
  </div>
</template>

<script>
import _ from 'lodash';
import EventBus from '@/EventBus';
import ApplicationEventTypes from '@/mixins/ApplicationEventTypes';
import ColorPicker from '@/components/ColorPicker/ColorPicker';
import BackgroundObject from '@/components/DesignObjects/BackgroundObject';
import { getMaxZIndex } from '@/components/Helper';
import panelContext from '@/components/LeftSection/mixins/panelContext';
import objectAlignment from '@/components/LeftSection/mixins/objectAlignment';
import objectEffects from '@/components/LeftSection/mixins/objectEffects';
import imageEffects from '@/components/LeftSection/mixins/imageEffects';
import objectTransform from '@/components/LeftSection/mixins/objectTransform';
import gradientFill from '@/components/LeftSection/mixins/gradientFill';
import sidePopover from '@/components/LeftSection/mixins/sidePopover';
import svgOutline from '@/components/LeftSection/mixins/svgOutline';
import FieldGroup from '@/components/LeftSection/ui/FieldGroup';
import PositionControls from '@/components/LeftSection/ui/PositionControls';
import RotateFlipControls from '@/components/LeftSection/ui/RotateFlipControls';
import EffectTiles from '@/components/LeftSection/ui/EffectTiles';
import EffectControlCard from '@/components/LeftSection/ui/EffectControlCard';
import SidePopover from '@/components/LeftSection/ui/SidePopover';
import FillColorSection from '@/components/LeftSection/ui/FillColorSection';

const SHAPE_EFFECT_OPTIONS = [
  { value: 'none', label: 'None', preview: 'none' },
  { value: 'outline', label: 'Outline', preview: 'outline' },
  { value: 'shadow', label: 'Shadow', preview: 'shadow' },
];

export default {
  name:       'ShapePanel',
  components: {
    'color-picker':         ColorPicker,
    'field-group':          FieldGroup,
    'position-controls':    PositionControls,
    'rotate-flip-controls': RotateFlipControls,
    'effect-tiles':         EffectTiles,
    'effect-control-card':  EffectControlCard,
    'side-popover':         SidePopover,
    'fill-color-section':   FillColorSection,
  },
  mixins: [panelContext, objectAlignment, objectEffects, imageEffects, objectTransform, gradientFill, sidePopover, svgOutline],
  props:  {
    width:       { type: Number, default: 0 },
    height:      { type: Number, default: 0 },
    productCrop: { type: Object, default: null },
    fillOpen:    { type: Boolean, default: true },
  },
  data() {
    return {
      shapeEffectOptions: SHAPE_EFFECT_OPTIONS,
    };
  },
  computed: {
    outlineSwatchColor() {
      return this.isMultiPropertySelection ? this.shapeStrokeColor : this.tempShapeOutlineColor;
    },
  },
  methods: {
    onShapeEffectSelect(value) {
      if (!this.isMultiPropertySelection && value === this.shapeEffectValue) { return; }
      if (value === 'outline') {
        this.applyOutlineOnShape();
      } else if (value === 'shadow') {
        this.applyShadowOnShape();
      } else {
        this.removeShapeEffect();
      }
    },
    onShapeShadowInput(payload) {
      if (payload.key === 'angle') {
        this.updateObjectShadowProperty('angle', parseInt(payload.value, 10));
      } else if (payload.key === 'distance') {
        this.updateObjectShadowProperty('distance', parseFloat(payload.value));
      } else if (payload.key === 'blur') {
        this.updateObjectShadowProperty('blur', payload.value);
      }
    },
    toggleShapeModel() {
      if (this.$store.getters.isShapeModel == false) {
        this.$store.commit('setShapeModel', true);
      } else {
        this.$store.commit('setShapeModel', false);
      }
    },
    addShapeObject(objectName) {
      EventBus.$emit('pauseVideoOnOutsideClick');

      switch (objectName) {
        case 'circle': {
          const { data }             = new BackgroundObject();
          const objectHeight         = this.width <= 125 ? 80 : 200;
          const objectWidth          = this.width <= 125 ? this.width / 1.5 : 200;
          data.shapeType             = 'ellipses';
          data.CurveVerticalRadius   = 100;
          data.CurveHorizontalRadius = 100;
          data.CurveCenterX          = 100;
          data.CurveCenterY          = 100;
          data.PdfSortOrder          = data.SortOrder = getMaxZIndex(document.getElementById('canvas-wrapper').firstElementChild.children) + 1;
          data.isSelected            = false;
          data.Alpha                 = 100;
          data.DefaultColor          = 'rgb(104, 129, 210)';
          data.Width                 = objectWidth;
          data.Height                = objectHeight;
          data.X                     = Math.abs((this.width / 2) - objectWidth / 2);
          data.Y                     = Math.abs((this.height / 2) - objectHeight / 2);
          data.isNewObject           = true;
          EventBus.$emit('onObjectAdding', _.cloneDeep(data));

          break;
        }
        case 'rectangle': {
          const { data }     = new BackgroundObject();
          const objectHeight = this.width <= 125 ? 80 : 178;
          const objectWidth  = this.width <= 125 ? this.width / 1.5 : 178;
          data.PdfSortOrder  = data.SortOrder = getMaxZIndex(document.getElementById('canvas-wrapper').firstElementChild.children) + 1;
          data.isSelected    = false;
          data.Alpha         = 100;
          data.DefaultColor  = 'rgb(104, 129, 210)';
          data.Width         = objectWidth;
          data.Height        = objectHeight;
          data.shapeType     = 'rectangle';
          data.X             = Math.abs((this.width / 2) - objectWidth / 2);
          data.Y             = Math.abs((this.height / 2) - objectHeight / 2);
          EventBus.$emit('onObjectAdding', _.cloneDeep(data));

          break;
        }
        default:
          break;
      }
      EventBus.$emit(ApplicationEventTypes.Canvas.CANVAS_RE_RENDER);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../styles/lp-tokens';
@import '../styles/lp-panel';

.lp-shape-panel {
  padding: 4px 4px 8px;
}

.lp-shape-panel__effect-card {
  margin-top: 10px;
}

.lp-shape-add {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.lp-shape-add__btn,
.lp-shape-library {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: $lp-control-h;
  padding: 0 12px;
  border-radius: $lp-radius-control;
  font-family: $lp-sans;
  font-size: $lp-fs-control;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.12s ease, box-shadow 0.12s ease;

  &:focus-visible {
    outline: 2px solid $lp-accent;
    outline-offset: 2px;
  }

  ::v-deep .icon,
  ::v-deep svg {
    width: 16px;
    height: 16px;
    min-width: 0;
    min-height: 0;
  }
}

.lp-shape-add__btn {
  border: 1px solid $lp-line;
  background: #fff;
  color: $lp-ink;

  ::v-deep svg path,
  ::v-deep svg rect {
    stroke: $lp-ink;
  }

  &:hover {
    background: $lp-fill;
  }
}

.lp-shape-library {
  width: 100%;
  margin-top: 8px;
  border: 0;
  background: $lp-accent;
  color: #fff;

  ::v-deep svg path,
  ::v-deep svg rect {
    stroke: #fff;
  }

  &:hover {
    background: $lp-accent-ink;
  }
}

.lp-shape-divider {
  height: 1px;
  margin: 20px 0 0;
  background: $lp-line;
}
</style>
